'use client';
import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useModel } from '@/context/ModelContext';

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ color: string; name: string; value: number }>;
  label?: string;
}) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      style={{
        background: 'var(--surface-2)',
        border: '1px solid var(--divider)',
        borderRadius: '4px',
        padding: '10px 14px',
      }}
    >
      <p
        style={{
          fontSize: '0.7rem',
          fontWeight: 700,
          color: 'var(--text-primary)',
          marginBottom: 6,
        }}
      >
        {label}
      </p>
      {payload.map((entry, idx) => (
        <div
          key={`ebitda-tooltip-${idx}`}
          className="flex items-center gap-2"
          style={{ marginBottom: 2 }}
        >
          <span
            className="w-2 h-2 rounded-sm flex-shrink-0"
            style={{ backgroundColor: entry.color }}
          />
          <span style={{ fontSize: '0.65rem', color: 'var(--text-secondary)' }}>{entry.name}:</span>
          <span
            style={{
              fontSize: '0.65rem',
              fontWeight: 700,
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            ₹{entry.value.toFixed(2)} Cr
          </span>
        </div>
      ))}
    </div>
  );
}

export default function EbitdaPatChart() {
  const { model } = useModel();

  const data = model.years.slice(1).map((y) => ({
    name: `Yr ${y.year}`,
    EBITDA: parseFloat((y.ebitda / 10000000).toFixed(2)),
    PAT: parseFloat((y.pat / 10000000).toFixed(2)),
  }));

  return (
    <ResponsiveContainer width="100%" height={240}>
      <AreaChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
        <defs>
          <linearGradient id="gradEbitda" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="var(--gold)" stopOpacity={0.2} />
            <stop offset="95%" stopColor="var(--gold)" stopOpacity={0.01} />
          </linearGradient>
          <linearGradient id="gradPat" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="var(--accent)" stopOpacity={0.2} />
            <stop offset="95%" stopColor="var(--accent)" stopOpacity={0.01} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="2 4" stroke="var(--divider)" vertical={false} />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 10, fill: 'var(--text-tertiary)', fontWeight: 600 }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tick={{ fontSize: 10, fill: 'var(--text-tertiary)' }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(v) => `₹${v}Cr`}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          wrapperStyle={{
            fontSize: '0.6rem',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            paddingTop: 8,
          }}
          iconType="square"
          iconSize={8}
          formatter={(v) => <span style={{ color: 'var(--text-secondary)' }}>{v}</span>}
        />
        <Area
          type="monotone"
          dataKey="EBITDA"
          stroke="var(--gold)"
          strokeWidth={2}
          fill="url(#gradEbitda)"
        />
        <Area
          type="monotone"
          dataKey="PAT"
          stroke="var(--accent)"
          strokeWidth={2}
          fill="url(#gradPat)"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
